import { Mail, Phone, MapPin, Linkedin, Globe, Github, Calendar, Award } from 'lucide-react'
import { getColorClasses } from '@/lib/templateColors'

interface ResumeTemplateProps {
  data: any
  language?: string
  labels: any
  color?: string
}


export default function LimeGreenTemplate({ data, labels, color = 'blue' }: ResumeTemplateProps) {
  const colors = getColorClasses(color)
  const { personalInfo, summary, experience, education, skills, projects, certifications } = data

  return (

      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
        <div id="resume-preview" className="bg-white" style={{ width: '8.5in', minHeight: '11in' }}>
          {/* Lime Green Header */}
          <div className={`bg-gradient-to-r ${colors.headerBg} ${colors.headerText} p-10`}>
            <h1 className="text-5xl font-black mb-4">{personalInfo.fullName || 'YOUR NAME'}</h1>
            <div className="grid grid-cols-3 gap-4 text-sm font-semibold">
              {personalInfo.email && <div className="flex items-center space-x-2"><Mail className="w-4 h-4" /><span>{personalInfo.email}</span></div>}
              {personalInfo.phone && <div className="flex items-center space-x-2"><Phone className="w-4 h-4" /><span>{personalInfo.phone}</span></div>}
              {personalInfo.address && <div className="flex items-center space-x-2"><MapPin className="w-4 h-4" /><span>{personalInfo.address}</span></div>}
            </div>
          </div>

          <div className="p-8">
            {summary && (
              <div className={`mb-6 ${colors.badgeBg} p-5 rounded-lg border-2 ${colors.sectionBorder}`}>
                <h2 className={`text-2xl font-black ${colors.sectionTitle} mb-3 uppercase`}>{labels.professionalSummary}</h2>
                <p className="text-gray-800 leading-relaxed font-medium">{summary}</p>
              </div>
            )}

            {experience && experience.length > 0 && (
              <div className="mb-6">
                <h2 className={`text-3xl font-black ${colors.sectionTitle} mb-5 uppercase`}>{labels.workExperience}</h2>
                <div className="space-y-6">
                  {experience.map((exp: any) => (
                    <div key={exp.id} className={`bg-gradient-to-r ${colors.badgeBg} p-5 rounded-lg border-l-8 ${colors.accentBorder}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="text-xl font-black text-gray-900">{exp.jobTitle}</h3>
                          <div className={`${colors.accentText} font-bold text-lg`}>{exp.company}</div>
                        </div>
                        <div className="text-sm text-gray-600 font-semibold">{exp.startDate} - {exp.endDate || labels.present}</div>
                      </div>
                      <div className="text-gray-700 whitespace-pre-line text-sm">{exp.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {skills && skills.length > 0 && (
              <div className="mb-6">
                <h2 className={`text-3xl font-black ${colors.sectionTitle} mb-5 uppercase`}>{labels.skills}</h2>
                <div className="space-y-4">
                  {skills.map((skillGroup: any, idx: number) => (
                    <div key={idx}>
                      <div className={`font-black ${colors.accentText} mb-3 text-xl`}>{skillGroup.category}</div>
                      <div className="flex flex-wrap gap-3">
                        {skillGroup.skills?.map((skill: string, skillIdx: number) => (
                          <span key={skillIdx} className={`${colors.accentBg} ${colors.badgeText} px-4 py-2 rounded-lg font-bold text-sm shadow-md`}>
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {education && education.length > 0 && (
              <div>
                <h2 className={`text-3xl font-black ${colors.sectionTitle} mb-5 uppercase`}>{labels.education}</h2>
                <div className="space-y-4">
                  {education.map((edu: any) => (
                    <div key={edu.id} className={`${colors.badgeBg} p-5 rounded-lg border-2 ${colors.sectionBorder}`}>
                      <div className="font-black text-gray-900 text-lg">{edu.degree}</div>
                      <div className={`${colors.accentText} font-bold`}>{edu.institution}</div>
                      <div className="text-sm text-gray-600 font-semibold">{edu.graduationDate}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
  )
}
